import { AcademicYearDTO, SiteDTO } from '@beable/roster-backend-client-v1';
import { Box, Button, Grid, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import Dropzone, { DropEvent } from 'react-dropzone';
import React, { useEffect, useState } from 'react';
import { uploadArchive } from '../../../services/FileUploadService';
import { getStyles } from './styles';

interface UploadProps {
  site: SiteDTO | undefined;
  academicYear: AcademicYearDTO | undefined;
}

export default function Upload({ site, academicYear }: UploadProps) {
  const theme = useTheme();
  const styles = getStyles(theme);
  const fileNamePattern = /[`!@#$%^&*()+=[\]{};':"\\|,<>/?~]/;
  const [selectedFile, setSelectedFile] = useState<File | undefined>();
  const [fileErr, setFileErr] = useState('');
  const [fileSuccess, setFileSuccess] = useState('');
  const [disableSend, setDisableSend] = useState(true);

  useEffect(() => {
    if (!site || !academicYear) {
      resetFileDropArea();
    }
  }, [site, academicYear]);

  useEffect(() => {
    if (site && selectedFile && academicYear) {
      setDisableSend(false);
    } else {
      setDisableSend(true);
    }
  }, [site, academicYear, selectedFile]);

  const handleFileSelect = (file: File, event: DropEvent) => {
    event.preventDefault();

    if (!file.name.includes('.csv')) {
      setErrorsOnLoad('Invalid file format! CSV files only');
      return;
    }

    if (file.name.trim().includes(' ')) {
      setErrorsOnLoad('Invalid file name! Cannot contain spaces.');
      return;
    }

    if (fileNamePattern.test(file.name)) {
      setErrorsOnLoad('File name must contain only alphanumeric values!');
      return;
    }

    setSelectedFile(file);
    setFileErr('');
    setFileSuccess('');
  };

  const uploadFile = async () => {
    setDisableSend(true);
    if (selectedFile && site) {
      const uploadResult = await uploadArchive(selectedFile, site, 'closedDates', academicYear?.schoolYear.toString());
      if (uploadResult && uploadResult.status === 201) {
        resetFileDropArea();
        setFileSuccess('Uploaded successfully!');
      } else {
        resetFileDropArea();
        setFileErr('Upload failed!');
      }
    }
  };

  const setErrorsOnLoad = (msg: string) => {
    setSelectedFile(undefined);
    setFileErr(msg);
    setFileSuccess('');
    setDisableSend(true);
  }

  const resetFileDropArea = () => {
    setSelectedFile(undefined);
    setFileErr('');
    setFileSuccess('');
    setDisableSend(true);
  }

  return (
    <Grid>
      <Dropzone
        onDrop={(acceptedFiles, _, event) => { handleFileSelect(acceptedFiles[0], event) }}
        multiple={false}
      >
        {({ getRootProps, getInputProps }) => (
          <section>
            <Box sx={styles.fileUpload} {...getRootProps()}>
              <input {...getInputProps()} />
              {selectedFile
                ? <Typography sx={styles.fileUploadText}>Your file: {selectedFile.name}</Typography>
                : <Typography sx={styles.fileUploadText}>
                  Drag and drop a Closed Dates CSV here, or click to select a file. <br />
                  Only *.csv files will be accepted. <br />
                  File name can not contain spaces.
                      </Typography>
              }
            </Box>
          </section>
        )}
      </Dropzone>
      <Box sx={styles.buttonsContainer}>
        <Button sx={styles.button} variant="contained" onClick={resetFileDropArea}>Clear</Button>
        <Button sx={styles.button} variant="contained" color="primary" disabled={disableSend} onClick={uploadFile}>
          Upload file
        </Button>
      </Box>
      {fileErr ? <Typography sx={{ ...styles.errMsg, textAlign: 'left' }} component="p">Error: {fileErr}</Typography> : undefined}
      {fileSuccess ? <Typography sx={{ ...styles.successMsg, textAlign: 'left' }} component="p">Status: {fileSuccess}</Typography> : undefined}
    </Grid>
  );
}
